import {Fragment, PropsWithChildren, ReactNode} from 'react';
import {useCompanyConfig} from 'hooks/useCompanyConfig';
import {FeatureFlags} from 'config/company-types';

type Props = {
  /**
   * The feature flag to check
   */
  feature: keyof FeatureFlags;
  /**
   * Optional fallback to render when feature is not enabled
   */
  fallback?: ReactNode;
} & PropsWithChildren<ReactNode>;

/**
 * Guard component that conditionally renders children based on feature flags
 *
 * @example
 * ```tsx
 * // Show landlord filter only for property managers
 * <FeatureGuard feature="hasLandlordFilter">
 *   <Select options={landlordOptions} />
 * </FeatureGuard>
 *
 * // With fallback
 * <FeatureGuard
 *   feature="hasPrescriptions"
 *   fallback={<p>Prescriptions not available</p>}
 * >
 *   <PrescriptionList />
 * </FeatureGuard>
 * ```
 */
export default function FeatureGuard({
  feature,
  children,
  fallback = null,
}: Props) {
  const {hasFeature} = useCompanyConfig();

  return hasFeature(feature) ? (
    <Fragment>{children}</Fragment>
  ) : (
    <>{fallback}</>
  );
}
